import { useEffect, useRef, useState } from 'react';
import { decodeAudioPeaks } from '@src/services/audioWaveform';

// A full download + Web Audio decode is far heavier than the trim metadata
// probe, so run fewer at once. Only VISIBLE blocks with audio reach here, but
// Fit zoom can make that the whole project — hence the bound.
const MAX_CONCURRENT = 3;
const MAX_CACHED = 200;

// Short block audios on a ~12px slot → a light peak density keeps every SVG path
// tiny even with dozens of clips on screen (maxPeaks caps a pathological block).
// `reload` bypasses the HTTP cache so the CORS fetch never reuses the no-ACAO
// copy that <audio> playback/trim cached without an Origin header (intermittent
// CORS otherwise — see services/audioWaveform).
const WAVEFORM_OPTS = {
  peaksPerSecond: 20,
  minPeaks: 24,
  maxPeaks: 240,
  reload: true,
};

// url → { peaks, duration }; in-flight decodes (url → Promise). Module-level so
// scrolling a block back into view reuses the decode; the pending map dedups a
// decode still running when the effect re-runs (e.g. scrolling).
const peaksCache = new Map();
const pendingCache = new Map();

const cachePeaks = (url, value) => {
  peaksCache.set(url, value);
  if (peaksCache.size > MAX_CACHED) {
    peaksCache.delete(peaksCache.keys().next().value);
  }
};

// Decode a url EXACTLY once, reusing a finished or in-flight result. Resolves
// null on skip/error (never rejects).
const measurePeaks = (url) => {
  if (peaksCache.has(url)) return Promise.resolve(peaksCache.get(url));
  if (pendingCache.has(url)) return pendingCache.get(url);

  const promise = decodeAudioPeaks(url, WAVEFORM_OPTS).then((value) => {
    if (value) cachePeaks(url, value);
    pendingCache.delete(url);
    return value;
  });
  pendingCache.set(url, promise);
  return promise;
};

// Decode many urls with bounded concurrency: MAX_CONCURRENT workers drain a
// shared queue. `onSettle` fires after EACH decode so the caller reveals
// waveforms progressively instead of waiting for the whole queue.
const measureAll = async (urls, onSettle) => {
  const queue = [...urls];
  const worker = async () => {
    while (queue.length) {
      // eslint-disable-next-line no-await-in-loop -- sequential per worker; concurrency comes from running several workers
      await measurePeaks(queue.shift());
      onSettle?.();
    }
  };
  const workers = Array.from(
    { length: Math.min(MAX_CONCURRENT, queue.length) },
    worker,
  );
  await Promise.all(workers);
};

const collectPeaks = (sources) => {
  const map = new Map();
  sources.forEach((source) => {
    const cached = peaksCache.get(source.audioLink);
    if (cached) map.set(source.id, cached);
  });
  return map;
};

// Cache entries are stable (immutable per url), so reference identity is enough.
const mapsEqual = (a, b) =>
  a.size === b.size &&
  Array.from(a).every(([id, value]) => b.get(id) === value);

/**
 * Decoded audio waveform ({ peaks, duration }) per block id. Decodes each url
 * once (bounded concurrency, cached + deduped) and fills in as decodes settle;
 * values are the shared cache entries so the returned map is referentially
 * stable between refreshes (React.memo-friendly). The consumer (Clip) slices the
 * peaks to the heard window for its own width. Mirrors useBlockTrimFlags —
 * waveform is just a heavier per-url read (full decode vs metadata duration),
 * sharing the decode with the media track via services/audioWaveform.
 *
 * @param {{ id: string, audioLink: string }[]} sources
 * @returns {Map<string, { peaks: number[], duration: number }>}
 */
const useBlockWaveforms = (sources) => {
  const [peaksById, setPeaksById] = useState(() => new Map());
  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;

  const signature = sources.map((s) => `${s.id}:${s.audioLink}`).join('|');

  useEffect(() => {
    let cancelled = false;
    const refresh = () =>
      setPeaksById((prev) => {
        const next = collectPeaks(sourcesRef.current);
        return mapsEqual(prev, next) ? prev : next;
      });

    refresh(); // instant for anything already decoded
    const urls = [
      ...new Set(
        sourcesRef.current
          .map((s) => s.audioLink)
          .filter((url) => url && !peaksCache.has(url)),
      ),
    ];
    measureAll(urls, () => {
      if (!cancelled) refresh();
    });

    return () => {
      cancelled = true;
    };
  }, [signature]);

  return peaksById;
};

export default useBlockWaveforms;
